import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Skeleton } from "../../../../components/ui/skeleton";
import { cn } from "../../../../lib/utils";
import { WindowPicker } from "../../hyperliquid/components/WindowPicker";
import { Pagination } from "../../hyperliquid/components/Pagination";
import { ErrorBanner } from "../../hyperliquid/components/ErrorBanner";
import { useCombinedStats, useCombinedUsers } from "../api/hooks";
import { useWindow } from "../../hyperliquid/hooks/useWindow";
import { CombinedUsersTable } from "../components/CombinedUsersTable";
import { formatInteger, formatUsd, formatDecimal } from "../../../../lib/format";

const DEFAULT_LIMIT = 20;

export function CombinedDashboard() {
  const { preset, setPreset, from, to, refresh, label } = useWindow();
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  const statsQuery = useCombinedStats({ from, to });
  const usersQuery = useCombinedUsers({ from, to, page, limit: DEFAULT_LIMIT });

  const handleRefresh = () => {
    refresh();
    queryClient.invalidateQueries({ queryKey: ["combined"] });
  };

  const handlePreset = (next) => {
    setPreset(next);
    setPage(1);
  };

  const windowLower = label.toLowerCase();

  return (
    <div className="space-y-14 md:space-y-16">
      <header className="flex flex-col gap-10 md:gap-12">
        {/* Title row */}
        <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              <span className="h-px w-6 bg-ginox-green" />
              Combined
            </div>
            <h1 className="mt-3 break-words font-serif text-[40px] italic leading-[1.02] tracking-tightest text-foreground sm:text-[48px] xl:text-[56px]">
              Combined <span className="text-gradient-ginox">overview</span>
            </h1>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <WindowPicker preset={preset} onChange={handlePreset} />
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw
                className={cn(
                  "h-3.5 w-3.5",
                  (statsQuery.isFetching || usersQuery.isFetching) &&
                    "animate-spin",
                )}
              />
              Refresh
            </Button>
          </div>
        </div>

        {/* Hero stat */}
        {statsQuery.error ? (
          <ErrorBanner
            error={statsQuery.error}
            onRetry={() => statsQuery.refetch()}
          />
        ) : (
          <HeroStat
            data={statsQuery.data}
            isLoading={statsQuery.isLoading}
            windowLower={windowLower}
          />
        )}
      </header>

      {/* Users */}
      <section className="space-y-6">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <h2 className="font-serif text-[22px] italic text-foreground">
            Users
          </h2>
          <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {usersQuery.data?.pagination.total != null ? (
              <>
                <span className="text-foreground">
                  {usersQuery.data.pagination.total}
                </span>{" "}
                total · {windowLower}
              </>
            ) : (
              "aggregates · current window"
            )}
          </p>
        </div>

        <div className="border-t border-border">
          {usersQuery.error ? (
            <div className="py-5">
              <ErrorBanner
                error={usersQuery.error}
                onRetry={() => usersQuery.refetch()}
              />
            </div>
          ) : (
            <>
              <CombinedUsersTable
                users={usersQuery.data?.users}
                isLoading={usersQuery.isLoading}
              />
              {usersQuery.data ? (
                <div className="border-t border-border">
                  <Pagination
                    data={usersQuery.data.pagination}
                    onChange={setPage}
                    label="users"
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}

function HeroStat({ data, isLoading, windowLower }) {
  const combined = data?.combined;

  return (
    <div className="flex min-w-0 flex-col gap-6 md:gap-7">
      {/* Primary: volume */}
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
          Volume · {windowLower}
        </p>
        {isLoading || !combined ? (
          <Skeleton className="mt-3 h-9 w-full max-w-[260px] sm:h-[52px] sm:max-w-[340px] xl:h-20 xl:max-w-[420px]" />
        ) : (
          <p
            data-tabular
            className="mt-3 max-w-full break-words font-mono text-[36px] font-medium leading-[1] tracking-tight text-foreground sm:text-[52px] xl:text-[80px]"
          >
            {formatUsd(combined.volume)}
          </p>
        )}
      </div>

      {/* Secondary: three inline stats with pipe dividers */}
      <div className="flex flex-wrap items-baseline gap-x-6 gap-y-4 border-t border-border pt-6 text-xs text-muted-foreground sm:gap-x-10">
        <SecondaryStat
          value={isLoading || !combined ? null : formatInteger(combined.fillCount)}
          label="fills executed"
        />
        <span className="hidden h-4 w-px self-center bg-border sm:block" />
        <SecondaryStat
          value={isLoading || !combined ? null : formatDecimal(combined.fees, 6)}
          label="fees earned"
        />
        <span className="hidden h-4 w-px self-center bg-border sm:block" />
        <SecondaryStat
          value={isLoading || !combined ? null : formatInteger(combined.totalUsers)}
          label="users on the fleet"
        />
      </div>
    </div>
  );
}

function SecondaryStat({ value, label }) {
  return (
    <div className="flex items-baseline gap-2">
      {value === null ? (
        <Skeleton className="h-5 w-16" />
      ) : (
        <span
          data-tabular
          className="font-mono text-[20px] font-medium leading-none tracking-tight text-foreground"
        >
          {value}
        </span>
      )}
      <span className="font-sans">{label}</span>
    </div>
  );
}
